import { Link } from 'react-router-dom';
import {
  EGB_REL_ID,
  CAB_ROLES,
  CAB_FUNCTIONS,
  CHANGE_MANAGEMENT,
  CHANGE_CLASSES,
  RELEASE_GOVERNANCE,
  RELEASE_READINESS,
  CONFIGURATION_MANAGEMENT,
  DATABASE_AI_CHANGES,
  MARKETPLACE_INFRA,
  ROLLBACK_RECOVERY,
  POST_RELEASE,
  CMDB,
  RELEASE_METRICS,
  EXECUTIVE_DASHBOARD,
  ACCESSIBILITY,
  ARCHITECTURE_STAGES,
  LIVE_ROUTES,
  DEMO_CAB,
  DEMO_CHANGE_MANAGEMENT,
  DEMO_CHANGE_CLASSIFICATION,
  DEMO_RELEASE_GOVERNANCE,
  DEMO_RELEASE_READINESS,
  DEMO_CONFIGURATION,
  DEMO_DATABASE_AI,
  DEMO_EXECUTIVE,
  buildReleaseDashboard,
} from '../../releasegovernance/framework';
import {
  ChangeAdvisoryBoardPanel,
  ChangeManagementPanel,
  ChangeClassificationPanel,
  ReleaseGovernancePanel,
  ReleaseReadinessPanel,
  ConfigurationManagementPanel,
  DatabaseAIChangePanel,
  ExecutiveReleaseDashboardPanel,
} from '../../components/releasegovernance';
import { Card, KpiCard } from '../../components/ui';

export function ReleaseGovernancePage() {
  const dashboard = buildReleaseDashboard();

  return (
    <div className="space-y-8">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-brand-600">{EGB_REL_ID}</p>
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">Enterprise Release Governance</h1>
        <p className="mt-1 text-sm text-slate-500">
          Permanent Release Governance Framework — change management, configuration, operational readiness, and controlled production delivery.
          Fully aligned with MPC-00001 and all Enterprise Governance Bibles.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          {Object.entries(LIVE_ROUTES).map(([key, path]) => (
            <Link
              key={key}
              to={path}
              className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-700 hover:bg-amber-100 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-300"
            >
              {path}
            </Link>
          ))}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <KpiCard label="CAB Roles" value={dashboard.council_roles.toString()} />
        <KpiCard label="Change Classes" value={dashboard.change_classes.toString()} />
        <KpiCard label="Readiness Gates" value={dashboard.readiness_gates.toString()} />
        <KpiCard label="Config Areas" value={dashboard.configuration_areas.toString()} />
        <KpiCard label="Governance Areas" value={dashboard.governance_areas.toString()} />
      </div>

      <section>
        <h2 className="text-lg font-semibold">Release Architecture</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          {ARCHITECTURE_STAGES.map((s, i) => (
            <span key={s} className="flex items-center gap-1 text-xs">
              <span className="rounded-lg border border-slate-200 px-2 py-1 dark:border-slate-700">{s}</span>
              {i < ARCHITECTURE_STAGES.length - 1 && <span className="text-slate-400">→</span>}
            </span>
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Change Advisory Board</h2>
        <p className="mt-1 text-sm text-slate-500">{CAB_ROLES.join(' · ')}</p>
        <p className="mt-1 text-xs text-slate-400">{CAB_FUNCTIONS.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <ChangeAdvisoryBoardPanel members={DEMO_CAB} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Change Management</h2>
        <p className="mt-1 text-sm text-slate-500">{CHANGE_MANAGEMENT.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <ChangeManagementPanel items={DEMO_CHANGE_MANAGEMENT} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Change Classification</h2>
        <p className="mt-1 text-sm text-slate-500">{CHANGE_CLASSES.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <ChangeClassificationPanel items={DEMO_CHANGE_CLASSIFICATION} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Release Governance</h2>
        <p className="mt-1 text-sm text-slate-500">{RELEASE_GOVERNANCE.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <ReleaseGovernancePanel items={DEMO_RELEASE_GOVERNANCE} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Release Readiness</h2>
        <p className="mt-1 text-sm text-slate-500">{RELEASE_READINESS.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <ReleaseReadinessPanel items={DEMO_RELEASE_READINESS} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Configuration Management</h2>
        <p className="mt-1 text-sm text-slate-500">{CONFIGURATION_MANAGEMENT.join(' · ')} · {MARKETPLACE_INFRA.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <ConfigurationManagementPanel items={DEMO_CONFIGURATION} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Database & AI Change Governance</h2>
        <p className="mt-1 text-sm text-slate-500">{DATABASE_AI_CHANGES.join(' · ')} · {ROLLBACK_RECOVERY.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <DatabaseAIChangePanel items={DEMO_DATABASE_AI} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Executive Release Dashboard</h2>
        <p className="mt-1 text-sm text-slate-500">
          {POST_RELEASE.join(' · ')} · {CMDB.join(' · ')} · {RELEASE_METRICS.join(' · ')} · {EXECUTIVE_DASHBOARD.join(' · ')} · {ACCESSIBILITY.join(' · ')}
        </p>
        <Card className="mt-4" padding="sm">
          <ExecutiveReleaseDashboardPanel items={DEMO_EXECUTIVE} />
        </Card>
      </section>
    </div>
  );
}
